import { CustomFieldValuesSection } from "@/shared/components/CustomFields";
import { TargetEntityType } from "@/shared/domain/customFields";
import { useReadyCustomFields } from "@/shared/domain/customFields/hooks";
import { useCheckFeatureFlag } from "@/shared/domain/featureFlags/hooks";

import styles from "./CreateSessionPage.module.css";

interface SessionCustomFieldsSectionProps {
  orgId: string;
  /** Draft custom-field values, owned by the create-session form. */
  values: Record<string, unknown>;
  onChange: (values: Record<string, unknown>) => void;
}

/**
 * Custom Fields card for the create-session page. Frames the shared
 * {@link CustomFieldValuesSection} in the page's own section chrome (a bordered
 * `.section` with an `<h2>` title) so it sits flush with the Name/Files/Devices
 * cards, which the shared section's flat, header-driven layout can't do.
 *
 * Gates the same way the shared section does — renders nothing when the
 * `release-custom-fields` flag is off or Sessions have no Ready fields — but
 * gates here too because only this component can hide the surrounding card, so
 * an empty state leaves no bare chrome.
 */
export function SessionCustomFieldsSection({
  orgId,
  values,
  onChange,
}: SessionCustomFieldsSectionProps) {
  const flagOn = useCheckFeatureFlag("release-custom-fields");
  const { data: readyFields } = useReadyCustomFields({
    entityType: TargetEntityType.Session,
    orgId,
    enabled: flagOn,
  });

  if (!flagOn || !readyFields?.length) {
    return null;
  }

  return (
    <section className={styles.section}>
      <div className={styles.sectionHeader}>
        <h2 className={styles.sectionTitle}>Custom Fields</h2>
      </div>
      <CustomFieldValuesSection
        entityType={TargetEntityType.Session}
        orgId={orgId}
        values={values}
        onChange={onChange}
      />
    </section>
  );
}
